import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, message, Space, Row, Col, } from "antd";
import api from "../../utils/api.js";

const { Option } = Select;

const GradeRegistrationForm = ({ visible, onCancel, onSubmit, currentProductId, currentProductName, user, lang }) => {
  const [form] = Form.useForm();
  const brandId = Form.useWatch('brandId', form);
  const companyId = Form.useWatch('companyId', form);
  const [companies, setCompanies] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const [nameValid, setNameValid] = useState(true);
  const [nameError, setNameError] = useState("");
  const [isBrandExist, setIsBrandExist] = useState(false);

  // 加载用户关联的企业
  useEffect(() => {
    if (visible && user?.id) {
      loadUserCompanies();
    }
  }, [visible, user?.id]);

  // 当企业变化时，加载对应品牌
  const handleCompanyChange = (companyId) => {
    if (companyId) {
      loadCompanyBrands(companyId);
    } else {
      setBrands([]);
      form.setFieldsValue({ brandId: undefined });
    }
  };

  const loadUserCompanies = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/users/${user.id}`);
      if (res && res.data && res.data.companies) {
        setCompanies(res.data.companies);
      }
    } catch (error) {
      console.error("Failed to load user companies:", error);
      message.error(lang ? "加载企业信息失败" : "Failed to load company information");
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyBrands = async (companyIds) => {
    try {
      setLoading(true);
      const res = await api.get("/brands", {
        params: { companyIds, productId: currentProductId }
      });
      if (res?.data?.list) {
        setBrands(res.data.list);
        form.setFieldsValue({ brandId: res.data.list[0]?.id || undefined });
      }
      if (res?.data?.totalCount === 0) {
        setIsBrandExist(false);
      } else {
        setIsBrandExist(true);
      }
    } catch (error) {
      console.error("Failed to load company brands:", error);
      message.error(lang ? "加载品牌信息失败" : "Failed to load brand information");
    } finally {
      setLoading(false);
    }
  };

  const checkGradeName = async (_, value) => {
    if (!value) {
      return Promise.resolve();
    }

    const brandId = form.getFieldValue("brandId");
    if (!brandId) {
      return Promise.resolve();
    }

    setCheckingName(true);
    try {
      // 检查牌号是否存在
      const res = await api.get("/grades", {
        params: { 
          brandIds: brandId, 
          productId: currentProductId, 
          search: value, // 使用精确匹配或后端支持的字段
          // 注意：这里假设后端 list 接口支持按名称搜索，且返回列表包含匹配项
          // 如果后端是模糊搜索，可能需要前端再精确比对一下
          isSimple: 1,
          pageSize: 10
        }
      });

      const list = res?.data?.list || [];
      console.log({list})
      // 假设搜索是模糊匹配，这里做一个精确匹配的检查 (不区分大小写)
      const exists = list.some(item => item.name && item.name.toLowerCase() === value.toLowerCase());

      if (exists) {
        setNameValid(false);
        setNameError(lang ? "该牌号已存在" : "Grade name already exists");
        return Promise.reject(new Error(lang ? "该牌号已存在" : "Grade name already exists"));
      }

      setNameValid(true);
      setNameError(lang ? "" : "");
      return Promise.resolve();
    } catch (error) {
      console.error("Failed to check grade name:", error);
      // 网络错误通常不阻塞校验，或者提示无法验证
      return Promise.resolve(); 
    } finally {
      setCheckingName(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const c = companies.find(c => c.id === values.companyId);
      if (!c) {
        message.error(lang ? "请选择企业" : "Please select the company you represent");
        return;
      }
      const payloadBrand = {nameCn: c.alias, nameEn: c.alias, alias: c.alias, companyId: c.id, productId: currentProductId};

      let payload = {
        brandId: values.brandId,
        name: values.name,
        productId: currentProductId,
      };
      if (!isBrandExist) payload = {...payload, ...payloadBrand, isBrandExist};

      const res = await api.post("/approvals/submit", {
        type: "CREATE_GRADE",
        payload: JSON.stringify(payload),
        remark: `申请登记新牌号${values.name}`
      });

      if (res?.data?.id) {
        message.success(lang ? "提交成功，等待审批" : "Submit success, waiting for approval");
        onSubmit();
        onCancel();
      } else {
        message.error(lang ? "提交失败，请重试" : "Submit failed, please try again");
      }
    } catch (error) {
      console.error("Failed to submit approval:", error);
      message.error(lang ? "提交失败，请重试" : "Submit failed, please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        productId: currentProductId
      }}
    >
      <Form.Item
        name="companyId"
        label={lang ? "您所代表的企业(可在用户档案中增选公司)" : "Company in representing"}
        rules={[{ required: true, message: lang ? "请选择企业您所代表的企业" : "Please select the company you represent" }]}
      >
        <Select
          placeholder={lang ? "请选择企业" : "Please select the company you represent"}
          loading={loading}
          disabled={loading}
          onChange={handleCompanyChange}
        >
          {companies.map(company => (
            <Option key={company.id} value={company.id}>
              {company.alias}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={lang ? "产品" : "Product"} style={{ marginBottom: 0 }}>
            <span style={{ lineHeight: '32px', color: 'var(--major-color)', fontWeight: 500 }}>
              {currentProductName || '-'}
            </span>
          </Form.Item>
        </Col>
        <Col span={12}>
            <Form.Item
              name="brandId"
              label={lang ? "品牌" : "Brand"}
            >
              {isBrandExist ? (
                <Select
                  placeholder={lang ? "请选择品牌" : "Please select the brand"}
                  loading={loading}
                  disabled={loading || !form.getFieldValue("companyId")}
                  rules={[{ required: true, message: lang ? "请选择企业旗下品牌" : "Please select the brand under the company" }]}
                >
                  {brands.map(brand => (
                    <Option key={brand.id} value={brand.id}>
                      {brand.alias}
                    </Option>
                  ))}
                </Select>
              ) : (
                <span style={{ lineHeight: '32px', color: 'var(--major-color)', fontWeight: 500 }}>
                  {lang ? "自动申请登记企业简称同名品牌" : "Automatically apply for registration of the brand with the same name as the company"}
                </span>
              )}
            </Form.Item>
        </Col>
      </Row>

      <Form.Item shouldUpdate>
        {() => (
          <Form.Item
            name="name"
            label={lang ? "牌号名称" : "Grade name"}
            validateTrigger="onBlur"
            rules={[
              { required: true, message: lang ? "请输入牌号名称" : "Please enter the grade name" },
              { validator: checkGradeName }
            ]}
          >
            <Input
              placeholder={lang ? "请输入牌号名称" : "Please enter the grade name"}
              disabled={loading || checkingName || !companyId}
              suffix={checkingName ? "检查中..." : null}
            />
          </Form.Item>
        )}
      </Form.Item>

      <Form.Item shouldUpdate>
        {() => (
          <Space>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              disabled={
                loading || 
                checkingName || 
                (!brandId && isBrandExist) || 
                !form.getFieldValue("name") || 
                form.getFieldsError().some(({ errors }) => errors.length)
              }
            >
              {lang ? "提交申请" : "Submit application"}
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={onCancel} disabled={loading}>
              {lang ? "取消" : "Cancel"}
            </Button>
          </Space>
        )}
      </Form.Item>
    </Form>
  );
};

export default GradeRegistrationForm;
